import { type ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, MonitorPlay } from 'lucide-react';
import { api, type SpaceSummary } from '../api';
import { StatusBadge } from '../components/StatusBadge';

export function SpaceTimeline(): ReactElement {
  const { id: spaceId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [t0At, setT0At] = useState('');
  const [t0Armed, setT0Armed] = useState(false);
  const [t0Saving, setT0Saving] = useState(false);

  // ── Load space ──────────────────────────────────────────────

  useEffect(() => {
    if (!spaceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.getSpace(spaceId);
        if (cancelled) return;
        setSpace(s);
        setT0Armed(s.scheduledT0Armed);
        if (s.scheduledT0At) {
          setT0At(new Date(s.scheduledT0At).toISOString().slice(0, 16));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [spaceId]);

  // ── Arm / Disarm ────────────────────────────────────────────

  const handleSetT0 = async (armed: boolean, at: string | null): Promise<void> => {
    if (!spaceId) return;
    setT0Saving(true);
    setError(null);
    try {
      await api.setScheduledT0(spaceId, { armed, at });
      const updated = await api.getSpace(spaceId);
      setSpace(updated);
      setT0Armed(updated.scheduledT0Armed);
      if (updated.scheduledT0At) {
        setT0At(new Date(updated.scheduledT0At).toISOString().slice(0, 16));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to set T0');
    } finally {
      setT0Saving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <section className="max-w-2xl">
        <p className="text-slate-400 text-sm">Loading…</p>
      </section>
    );
  }

  if (!space) {
    return (
      <section className="max-w-2xl">
        <p className="text-rose-400 text-sm">Space not found.</p>
      </section>
    );
  }

  return (
    <section className="space-y-4 max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/spaces')}
            aria-label="Back to spaces"
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Back
          </button>
          <h1 className="text-lg font-semibold">{space.title}</h1>
          <StatusBadge status={space.status} />
        </div>
        <Link
          to={`/spaces/${space.id}/simulation`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-accent text-slate-900 text-sm font-medium hover:bg-accent/90 transition-colors"
        >
          <MonitorPlay size={16} />
          Open Simulation
        </Link>
      </div>

      {error && <p className="text-rose-400 text-sm">{error}</p>}

      {/* T0 Configuration */}
      <section className="bg-panel rounded-lg border border-slate-800 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <Clock size={16} />
          Scheduled T0 Anchor
        </h2>

        <p className="text-xs text-slate-500">
          Set a wall-clock anchor for deferred intents. Agents submit intents
          with offsets relative to this T0. When unarmed, the UE client connection
          time is used as the implicit anchor.
        </p>

        {/* Datetime picker + Arm/Disarm */}
        <div className="flex items-center gap-3">
          <input
            type="datetime-local"
            value={t0At}
            onChange={(e) => setT0At(e.target.value)}
            disabled={t0Saving}
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 disabled:opacity-50"
          />
          {t0Armed ? (
            <button
              type="button"
              onClick={() => void handleSetT0(false, null)}
              disabled={t0Saving}
              className="px-4 py-1.5 rounded bg-amber-700 text-amber-100 text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {t0Saving ? '…' : 'Disarm'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                void handleSetT0(true, t0At ? new Date(t0At).toISOString() : null)
              }
              disabled={t0Saving || !t0At}
              className="px-4 py-1.5 rounded bg-accent text-slate-900 text-sm font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {t0Saving ? '…' : 'Arm'}
            </button>
          )}
        </div>

        {/* Status */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span>
            Status:{' '}
            <span className={t0Armed ? 'text-emerald-400 font-medium' : 'text-slate-400'}>
              {t0Armed ? 'ARMED' : 'disarmed'}
            </span>
          </span>
          {space.scheduledT0At && (
            <span>
              Current T0:{' '}
              <span className="text-slate-300">
                {new Date(space.scheduledT0At).toLocaleString()}
              </span>
            </span>
          )}
          {!t0Armed && space.currentSessionConnectedAt && (
            <span>
              Fallback (session):{' '}
              <span className="text-slate-300">
                {new Date(space.currentSessionConnectedAt).toLocaleString()}
              </span>
            </span>
          )}
        </div>
      </section>

      {/* Quick links */}
      <section className="flex items-center gap-3">
        <Link
          to={`/spaces/${space.id}`}
          className="text-sm text-accent hover:underline"
        >
          Edit Space
        </Link>
        <Link
          to={`/spaces/${space.id}/dashboard`}
          className="text-sm text-accent hover:underline"
        >
          Dashboard
        </Link>
        <Link
          to={`/spaces/${space.id}/actors`}
          className="text-sm text-accent hover:underline"
        >
          Actors
        </Link>
      </section>
    </section>
  );
}
